import { getCatalogRamSnapshotCache } from '../../catalog/infra/catalogRamSnapshotCache';
import { getAppSettingSafe, persistAppSettingSafe } from './persistAppSetting';
import {
  MASTERS_SYNC_COOLDOWN_MS,
  MASTERS_BACKUP_INTERVAL_MS,
} from './masterSyncPolicyConstants';

// Re-export bindings (no `export … from`) so Metro no lea props de un module
// incompleto cuando hay require cycle vía pullDownHelpers → catalog → sync.
export { MASTERS_SYNC_COOLDOWN_MS, MASTERS_BACKUP_INTERVAL_MS };

let mastersSyncedAtMemory = 0;

export function markMastersSyncedOkMemory(at = Date.now()): void {
  mastersSyncedAtMemory = at;
}

function getMastersSyncedAtMemory(): number {
  return mastersSyncedAtMemory;
}

function resetMastersSyncPolicyMemory(): void {
  mastersSyncedAtMemory = 0;
}

/** Tests: limpia memoria de cooldown maestros. */
export function resetMastersSyncPolicyMemoryForTests(): void {
  resetMastersSyncPolicyMemory();
}

export function wasMastersSyncedRecentlyMemory(now = Date.now()): boolean {
  if (mastersSyncedAtMemory <= 0) return false;
  return now - mastersSyncedAtMemory < MASTERS_SYNC_COOLDOWN_MS;
}

export const MASTERS_LAST_OK_AT_KEY = 'sync.masters_last_ok_at';

export function markMastersSyncedOk(at = Date.now()): void {
  markMastersSyncedOkMemory(at);
  void persistAppSettingSafe(MASTERS_LAST_OK_AT_KEY, new Date(at).toISOString());
}

/** Logout: memoria de cooldown maestros (persistido en app_settings sobrevive al F5). */
export function resetMastersSyncPolicyOnLogout(): void {
  resetMastersSyncPolicyMemory();
}

async function readMastersLastOkAtMs(): Promise<number> {
  const memory = getMastersSyncedAtMemory();
  if (memory > 0) return memory;
  const raw = await getAppSettingSafe(MASTERS_LAST_OK_AT_KEY);
  if (!raw) return 0;
  const parsed = Date.parse(raw);
  return Number.isFinite(parsed) ? parsed : 0;
}

/** True si un pull automático de catálogo corrió hace menos de MASTERS_SYNC_COOLDOWN_MS. */
export async function wasMastersSyncedRecently(now = Date.now()): Promise<boolean> {
  if (wasMastersSyncedRecentlyMemory(now)) return true;
  const lastOk = await readMastersLastOkAtMs();
  if (lastOk <= 0) return false;
  return now - lastOk < MASTERS_SYNC_COOLDOWN_MS;
}

/** Primer sync en dispositivo: nunca hubo pull de catálogo persistido. */
export async function isFirstCatalogSyncNeeded(): Promise<boolean> {
  // Lazy: evita cycle masterSyncPolicy → pullDownHelpers → catalog → masterSyncPolicy
  // que en Metro dejaba MASTERS_SYNC_COOLDOWN_MS en undefined al boot.
  const { getCatalogLastPullAt } = await import('./pullDownHelpers');
  const lastPull = await getCatalogLastPullAt();
  return lastPull == null || lastPull.trim() === '';
}

/** ¿Debería intentarse pull de catálogo en refresh automático? */
export async function shouldAutoPullCatalog(opts?: {
  force?: boolean;
  forceCatalog?: boolean;
  now?: number;
  localProductsCount?: number | null;
}): Promise<boolean> {
  if (opts?.force || opts?.forceCatalog) return true;
  if (opts?.localProductsCount === 0) return true;
  if (await isFirstCatalogSyncNeeded()) return true;
  return !(await wasMastersSyncedRecently(opts?.now));
}

export async function getLocalCatalogProductsCount(): Promise<number | null> {
  return getCatalogRamSnapshotCache()?.products.length ?? 0;
}

